import { useMemo, useState } from 'react';
import { api, ApiError, formatDateTime, scr } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import type { BlueGrant, BlueGrantMeReport, BlueGrantMilestone, BlueGrantScheme, BlueGrantScore, Page } from '../../lib/types';
import { useApiQuery } from '../../lib/useApi';
import { Detail, EmptyState, ErrorNote, Field, Modal, PageHeader, RoleNotice, Spinner, StatusBadge, TabStrip } from '../../ui/components';

const GRANT_SECTORS = ['SEAWEED_FARMING', 'AQUACULTURE', 'MARINE_RENEWABLE_ENERGY', 'MARINE_RESEARCH', 'PLASTIC_CIRCULAR_ECONOMY', 'MARINE_ECOTOURISM', 'OTHER'] as const;

type Tab = 'applications' | 'schemes';

/** M34 Blue Economy — blue-economy grant panel: claim, score, decide, release milestone disbursements via SeyPay, review M&E reports. */
export function BlueGrants() {
  const [tab, setTab] = useState<Tab>('applications');
  return (
    <div>
      <PageHeader title="Blue Economy Grants" subtitle="Panel scoring, approval, milestone disbursement, and M&E review (M34)." />
      <TabStrip tabs={[{ key: 'applications', label: 'Applications' }, { key: 'schemes', label: 'Schemes' }]} active={tab} onChange={setTab} />
      {tab === 'applications' ? <ApplicationsTab /> : <SchemesTab />}
    </div>
  );
}

// ── Applications ─────────────────────────────────────────────────────────

function ApplicationsTab() {
  const { persona } = useAuth();
  const isPanel = persona ? persona.roles.includes('GRANTS_PANEL') : false;
  const [status, setStatus] = useState('');
  const [q, setQ] = useState('');
  const [appliedQ, setAppliedQ] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const path = useMemo(() => {
    const params = new URLSearchParams({ limit: '50' });
    if (status) params.set('status', status);
    if (appliedQ) params.set('q', appliedQ);
    return `/api/v1/m34/blue-grants?${params.toString()}`;
  }, [status, appliedQ]);
  const listQ = useApiQuery<Page<BlueGrant>>(path);

  return (
    <div>
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <form
          className="flex items-center gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            setAppliedQ(q);
          }}
        >
          <input className="field-input !w-56" placeholder="Ref, project title, applicant…" value={q} onChange={(e) => setQ(e.target.value)} />
          <select className="field-input !w-auto" value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value="">All statuses</option>
            {['DRAFT', 'SUBMITTED', 'UNDER_REVIEW', 'SCORED', 'APPROVED', 'REJECTED', 'DISBURSING', 'COMPLETED', 'WITHDRAWN'].map((s) => (
              <option key={s} value={s}>
                {s.replaceAll('_', ' ')}
              </option>
            ))}
          </select>
          <button type="submit" className="btn-secondary">
            Search
          </button>
        </form>
      </div>
      {!isPanel ? <RoleNotice roles={['GRANTS_PANEL']} /> : null}
      {listQ.error ? <ErrorNote message={listQ.error} /> : null}
      <div className="card overflow-x-auto">
        {listQ.loading ? (
          <Spinner />
        ) : (listQ.data?.items.length ?? 0) === 0 ? (
          <EmptyState title="No grant applications match" />
        ) : (
          <table className="w-full">
            <thead>
              <tr>
                <th className="table-head">Ref</th>
                <th className="table-head">Project</th>
                <th className="table-head">Requested</th>
                <th className="table-head">Score avg</th>
                <th className="table-head">Status</th>
              </tr>
            </thead>
            <tbody>
              {listQ.data!.items.map((g) => (
                <tr key={g.id} className="cursor-pointer hover:bg-slate-50" onClick={() => setSelectedId(g.id)}>
                  <td className="table-cell font-mono text-xs font-medium">{g.refNo}</td>
                  <td className="table-cell">
                    {g.projectTitle}
                    <div className="text-[11px] text-slate-400">{g.applicantName}</div>
                  </td>
                  <td className="table-cell text-xs">{scr(g.requestedAmountScr)}</td>
                  <td className="table-cell text-xs">{g.scoreAverage ?? '—'}</td>
                  <td className="table-cell">
                    <StatusBadge status={g.status} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      {selectedId ? (
        <GrantDetailModal
          id={selectedId}
          isPanel={isPanel}
          onClose={() => setSelectedId(null)}
          onChanged={() => listQ.reload()}
        />
      ) : null}
    </div>
  );
}

function GrantDetailModal({ id, isPanel, onClose, onChanged }: { id: string; isPanel: boolean; onClose: () => void; onChanged: () => void }) {
  const gq = useApiQuery<BlueGrant>(`/api/v1/m34/blue-grants/${id}`);
  const scoresQ = useApiQuery<{ items: BlueGrantScore[] }>(isPanel ? `/api/v1/m34/blue-grants/${id}/scores` : null);
  const milestonesQ = useApiQuery<{ items: BlueGrantMilestone[] }>(`/api/v1/m34/blue-grants/${id}/milestones`);
  const meReportsQ = useApiQuery<{ items: BlueGrantMeReport[] }>(`/api/v1/m34/blue-grants/${id}/me-reports`);
  const g = gq.data;
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [scoreVal, setScoreVal] = useState('');
  const [scoreComment, setScoreComment] = useState('');
  const [showDecision, setShowDecision] = useState<'APPROVED' | 'REJECTED' | null>(null);
  const [decisionReason, setDecisionReason] = useState('');
  const [approvedAmount, setApprovedAmount] = useState('');
  const [reviewingReportId, setReviewingReportId] = useState<string | null>(null);
  const [reportOutcome, setReportOutcome] = useState<'APPROVED' | 'QUERIED' | 'REJECTED'>('APPROVED');
  const [reportNotes, setReportNotes] = useState('');

  const refreshAll = () => {
    gq.reload();
    scoresQ.reload();
    milestonesQ.reload();
    meReportsQ.reload();
    onChanged();
  };
  const call = async (fn: () => Promise<unknown>) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
      refreshAll();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Request failed');
    } finally {
      setBusy(false);
    }
  };
  const claim = () => call(() => api.post(`/api/v1/m34/blue-grants/${id}/claim`));
  const submitScore = () => call(() => api.post(`/api/v1/m34/blue-grants/${id}/scores`, { score: Number.parseFloat(scoreVal), comment: scoreComment || undefined })).then(() => { setScoreVal(''); setScoreComment(''); });
  const finalizeScoring = () => call(() => api.post(`/api/v1/m34/blue-grants/${id}/finalize-scoring`));
  const decide = () =>
    call(() =>
      api.post(`/api/v1/m34/blue-grants/${id}/decide`, {
        outcome: showDecision,
        reason: decisionReason,
        approvedAmountScr: showDecision === 'APPROVED' && approvedAmount ? Number.parseFloat(approvedAmount) : undefined,
      }),
    ).then(() => setShowDecision(null));
  const releaseMilestone = (milestoneId: string) => call(() => api.post(`/api/v1/m34/blue-grants/${id}/milestones/${milestoneId}/release`));
  const reviewReport = (reportId: string) =>
    call(() => api.post(`/api/v1/m34/blue-grants/${id}/me-reports/${reportId}/review`, { outcome: reportOutcome, notes: reportNotes || undefined })).then(() => {
      setReviewingReportId(null);
      setReportNotes('');
    });

  return (
    <Modal title={g ? `${g.refNo} — ${g.projectTitle}` : 'Blue grant application'} onClose={onClose} wide>
      {gq.error ? <ErrorNote message={gq.error} /> : null}
      {gq.loading ? <Spinner /> : null}
      {error ? (
        <div className="mb-3">
          <ErrorNote message={error} />
        </div>
      ) : null}
      {g ? (
        <div className="space-y-5">
          <div className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm sm:grid-cols-3">
            <Detail label="Status" value={<StatusBadge status={g.status} />} />
            <Detail label="Applicant" value={`${g.applicantType}:${g.applicantId}`} mono />
            <Detail label="Duration" value={`${g.durationMonths} months`} />
            <Detail label="Requested" value={scr(g.requestedAmountScr)} />
            <Detail label="Approved" value={scr(g.approvedAmountScr)} />
            <Detail label="Co-financing" value={scr(g.coFinancingScr)} />
            <Detail label="Score average" value={g.scoreAverage ?? '—'} />
            <Detail label="Submitted" value={formatDateTime(g.submittedAt)} />
            <Detail label="Agreement (DocVault)" value={g.agreementDocRef ?? '—'} mono />
          </div>
          <Detail label="Proposal summary" value={g.proposalSummary} />
          {g.panelDecisionReason ? <Detail label="Panel decision note" value={g.panelDecisionReason} /> : null}
          {g.withdrawnReason ? <ErrorNote message={`Withdrawn: ${g.withdrawnReason}`} /> : null}

          {isPanel && g.status === 'UNDER_REVIEW' ? (
            <div>
              <div className="field-label">Panel scores ({scoresQ.data?.items.length ?? 0})</div>
              {(scoresQ.data?.items ?? []).map((s) => (
                <div key={s.id} className="flex justify-between border-b border-slate-100 py-1 text-sm">
                  <span className="font-mono text-xs text-slate-500">{s.panelistId}</span>
                  <span>
                    {s.score} {s.comment ? `— ${s.comment}` : ''}
                  </span>
                </div>
              ))}
              <div className="mt-2 flex gap-2">
                <input className="field-input !w-24" type="number" min="0" max="100" placeholder="Score" value={scoreVal} onChange={(e) => setScoreVal(e.target.value)} />
                <input className="field-input flex-1" placeholder="Comment (optional)" value={scoreComment} onChange={(e) => setScoreComment(e.target.value)} />
                <button className="btn-primary" disabled={busy || !scoreVal} onClick={submitScore}>
                  Submit score
                </button>
              </div>
            </div>
          ) : null}

          {milestonesQ.data && milestonesQ.data.items.length > 0 ? (
            <div>
              <div className="field-label">Disbursement milestones</div>
              <table className="w-full text-sm">
                <thead>
                  <tr>
                    <th className="table-head">#</th>
                    <th className="table-head">Description</th>
                    <th className="table-head">Amount</th>
                    <th className="table-head">Status</th>
                    {isPanel ? <th className="table-head"></th> : null}
                  </tr>
                </thead>
                <tbody>
                  {milestonesQ.data.items.map((m) => (
                    <tr key={m.id}>
                      <td className="table-cell">{m.sequenceNo}</td>
                      <td className="table-cell">{m.description}</td>
                      <td className="table-cell">{scr(m.amountScr)}</td>
                      <td className="table-cell">
                        <StatusBadge status={m.status} />
                      </td>
                      {isPanel ? (
                        <td className="table-cell">
                          {m.status === 'PENDING' && g.status === 'DISBURSING' ? (
                            <button className="text-xs font-semibold text-brand-700 underline" disabled={busy} onClick={() => releaseMilestone(m.id)}>
                              Release
                            </button>
                          ) : null}
                        </td>
                      ) : null}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : null}

          {meReportsQ.data && meReportsQ.data.items.length > 0 ? (
            <div>
              <div className="field-label">M&amp;E reports</div>
              <div className="space-y-2">
                {meReportsQ.data.items.map((r) => (
                  <div key={r.id} className="rounded-lg border border-slate-200 p-2.5 text-sm">
                    <div className="flex items-center justify-between">
                      <StatusBadge status={r.status} />
                      <span className="text-xs text-slate-500">{scr(r.amountSpentScr)} spent</span>
                    </div>
                    <p className="mt-1 text-xs text-slate-600">{r.narrative}</p>
                    {isPanel && r.status === 'SUBMITTED' ? (
                      reviewingReportId === r.id ? (
                        <div className="mt-2 space-y-2">
                          <select className="field-input" value={reportOutcome} onChange={(e) => setReportOutcome(e.target.value as typeof reportOutcome)}>
                            <option value="APPROVED">Approve</option>
                            <option value="QUERIED">Query</option>
                            <option value="REJECTED">Reject</option>
                          </select>
                          <textarea className="field-input" rows={2} placeholder="Notes" value={reportNotes} onChange={(e) => setReportNotes(e.target.value)} />
                          <div className="flex gap-2">
                            <button className="btn-secondary" onClick={() => setReviewingReportId(null)}>
                              Cancel
                            </button>
                            <button className="btn-primary" disabled={busy} onClick={() => reviewReport(r.id)}>
                              Submit review
                            </button>
                          </div>
                        </div>
                      ) : (
                        <button className="mt-2 text-xs font-semibold text-brand-700 underline" onClick={() => setReviewingReportId(r.id)}>
                          Review report
                        </button>
                      )
                    ) : null}
                  </div>
                ))}
              </div>
            </div>
          ) : null}

          {isPanel ? (
            <div className="border-t border-slate-100 pt-4">
              {!showDecision ? (
                <div className="flex flex-wrap gap-2">
                  {g.status === 'SUBMITTED' ? (
                    <button className="btn-primary" disabled={busy} onClick={claim}>
                      Claim for review
                    </button>
                  ) : null}
                  {g.status === 'UNDER_REVIEW' ? (
                    <button className="btn-primary" disabled={busy} onClick={finalizeScoring}>
                      Finalize scoring
                    </button>
                  ) : null}
                  {g.status === 'SCORED' ? (
                    <>
                      <button className="btn-success" disabled={busy} onClick={() => setShowDecision('APPROVED')}>
                        Approve
                      </button>
                      <button className="btn-danger" disabled={busy} onClick={() => setShowDecision('REJECTED')}>
                        Reject
                      </button>
                    </>
                  ) : null}
                  {g.status === 'UNDER_REVIEW' ? (
                    <button className="btn-danger" disabled={busy} onClick={() => setShowDecision('REJECTED')}>
                      Reject
                    </button>
                  ) : null}
                </div>
              ) : (
                <div className="space-y-2 rounded-lg border border-slate-200 p-3">
                  {showDecision === 'APPROVED' ? (
                    <Field label="Approved amount (SCR)">
                      <input className="field-input" type="number" placeholder={String(g.requestedAmountScr)} value={approvedAmount} onChange={(e) => setApprovedAmount(e.target.value)} />
                    </Field>
                  ) : null}
                  <Field label="Decision note/reason">
                    <textarea className="field-input" rows={2} value={decisionReason} onChange={(e) => setDecisionReason(e.target.value)} />
                  </Field>
                  <div className="flex gap-2">
                    <button className="btn-secondary" onClick={() => setShowDecision(null)}>
                      Cancel
                    </button>
                    <button
                      className={showDecision === 'APPROVED' ? 'btn-success' : 'btn-danger'}
                      disabled={busy || decisionReason.trim().length < 3 || (showDecision === 'APPROVED' && !approvedAmount)}
                      onClick={decide}
                    >
                      {busy ? 'Submitting…' : 'Confirm'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          ) : null}
        </div>
      ) : null}
    </Modal>
  );
}

// ── Schemes ───────────────────────────────────────────────────────────────

function SchemesTab() {
  const { persona } = useAuth();
  const isPanel = persona ? persona.roles.includes('GRANTS_PANEL') : false;
  const [includeInactive, setIncludeInactive] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const path = useMemo(() => `/api/v1/m34/blue-grant-schemes${includeInactive ? '?active=false' : ''}`, [includeInactive]);
  const listQ = useApiQuery<{ items: BlueGrantScheme[] }>(path);

  const toggleActive = async (s: BlueGrantScheme) => {
    await api.patch(`/api/v1/m34/blue-grant-schemes/${s.id}`, { active: !s.active });
    listQ.reload();
  };

  return (
    <div>
      <div className="mb-4 flex items-center justify-between">
        <label className="flex items-center gap-2 text-sm text-slate-600">
          <input type="checkbox" checked={includeInactive} onChange={(e) => setIncludeInactive(e.target.checked)} />
          Show inactive schemes too
        </label>
        {isPanel ? (
          <button className="btn-primary" onClick={() => setShowCreate(true)}>
            + New scheme
          </button>
        ) : null}
      </div>
      {listQ.error ? <ErrorNote message={listQ.error} /> : null}
      <div className="card overflow-x-auto">
        {listQ.loading ? (
          <Spinner />
        ) : (listQ.data?.items.length ?? 0) === 0 ? (
          <EmptyState title="No grant schemes configured" />
        ) : (
          <table className="w-full">
            <thead>
              <tr>
                <th className="table-head">Code</th>
                <th className="table-head">Name</th>
                <th className="table-head">Sector</th>
                <th className="table-head">Max amount</th>
                <th className="table-head">Active</th>
                {isPanel ? <th className="table-head"></th> : null}
              </tr>
            </thead>
            <tbody>
              {listQ.data!.items.map((s) => (
                <tr key={s.id}>
                  <td className="table-cell font-mono text-xs font-medium">{s.code}</td>
                  <td className="table-cell">{s.name}</td>
                  <td className="table-cell text-xs">{s.sector.replaceAll('_', ' ')}</td>
                  <td className="table-cell text-xs">{scr(s.maxAmountScr)}</td>
                  <td className="table-cell">
                    <StatusBadge status={s.active ? 'ACTIVE' : 'CLOSED'} />
                  </td>
                  {isPanel ? (
                    <td className="table-cell">
                      <button className="text-xs font-semibold text-brand-700 underline" onClick={() => toggleActive(s)}>
                        {s.active ? 'Deactivate' : 'Activate'}
                      </button>
                    </td>
                  ) : null}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      {showCreate ? (
        <NewSchemeModal
          onClose={() => setShowCreate(false)}
          onCreated={() => {
            setShowCreate(false);
            listQ.reload();
          }}
        />
      ) : null}
    </div>
  );
}

function NewSchemeModal({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [sector, setSector] = useState<(typeof GRANT_SECTORS)[number]>('AQUACULTURE');
  const [description, setDescription] = useState('');
  const [maxAmountScr, setMaxAmountScr] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.post('/api/v1/m34/blue-grant-schemes', { code, name, sector, description: description || undefined, maxAmountScr: Number.parseFloat(maxAmountScr) });
      onCreated();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Request failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="New blue-economy grant scheme" onClose={onClose} wide>
      {error ? (
        <div className="mb-3">
          <ErrorNote message={error} />
        </div>
      ) : null}
      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <Field label="Code">
            <input className="field-input" value={code} onChange={(e) => setCode(e.target.value)} placeholder="BLUE_AQUA_2026" />
          </Field>
          <Field label="Sector">
            <select className="field-input" value={sector} onChange={(e) => setSector(e.target.value as (typeof GRANT_SECTORS)[number])}>
              {GRANT_SECTORS.map((s) => (
                <option key={s} value={s}>
                  {s.replaceAll('_', ' ')}
                </option>
              ))}
            </select>
          </Field>
        </div>
        <Field label="Name">
          <input className="field-input" value={name} onChange={(e) => setName(e.target.value)} />
        </Field>
        <Field label="Description (optional)">
          <textarea className="field-input" rows={2} value={description} onChange={(e) => setDescription(e.target.value)} />
        </Field>
        <Field label="Max amount (SCR)">
          <input className="field-input" type="number" value={maxAmountScr} onChange={(e) => setMaxAmountScr(e.target.value)} />
        </Field>
        <button className="btn-primary w-full" disabled={busy || !code || !name || !maxAmountScr} onClick={submit}>
          {busy ? 'Creating…' : 'Create scheme'}
        </button>
      </div>
    </Modal>
  );
}
